/* ─────────────────────────────────────────────────────────────
   SECTOR CONTEXT TAB
   GET /api/v1/stocks/{ticker}/sector-context

   Replicates the reference note's Sector-Primer panel section by section:

     market dynamics          ← note.market_dynamics
     competitive landscape    ← note.competitive_landscape[]
     industry averages        ← industry_avg (dynamic dict, RAW ratios)
     peer comps spread        ← top_peers[] + self_comps (forward multiples,
                                 fwd EPS growth, gross margin, model rank)
     read on PEG              ← peg_read (templated server-side; null when the
                                 stock's own PEG is "n/m" — box hidden then)
     top peers by grade       ← top_peers[] overall_score / overall_grade
     thematic shortlist       ← note.thematic_shortlist[]
     sector risk              ← note.sector_risk

   The note.* prose comes from the stored per-quarter earnings note and is null
   until its seeder has run — render what's present, hide what's null.
───────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip as MuiTooltip } from "@mui/material";
import { getSectorContext } from "../../../api/stocks_api";
import {
  humanizeKey, formatRatio, fmtNum, fmtPeg, fmtMultiple, fmtRank, fmtPct,
} from "../../../config/earningsNote";
import { formatCurrency } from "../../../utils/currency";
import {
  NotePage, SectionTitle, Card, Banner, Pill, GradeChip,
  Scroller, Table, Th, Td, Tl, NoteSkeleton, NoteEmpty,
  NoteMasthead, NoteFooter, ProseSection, NoteList,
} from "../Widgets/earningsNoteUI";

const SectorContext = ({ stock }) => {
  const ticker = stock?.ticker;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ error: false, notFound: false });

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setState({ error: false, notFound: false });
      const res = await getSectorContext(ticker);
      if (cancelled) return;
      if (res?.success) setData(res.data);
      else setState({ error: !res?.notFound, notFound: !!res?.notFound });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  if (loading) return <NoteSkeleton blocks={[80, 160, 260]} />;
  if (state.notFound) return <NoteEmpty>{ticker} is not in coverage.</NoteEmpty>;
  if (state.error || !data)
    return <NoteEmpty>Sector context is not available for {ticker}.</NoteEmpty>;

  const {
    sector_name, industry_name, industry_avg, top_peers = [],
    self_comps, peg_read, note,
  } = data;

  /* The comps spread includes the stock itself, highlighted — the API keeps
     its row out of top_peers and serves it separately as self_comps. */
  const compsRows = [
    ...(self_comps ? [{ ...self_comps, company_name: stock?.company_name, isSelf: true }] : []),
    ...top_peers,
  ].filter((r) => r?.fwd_pe != null || r?.peg != null || r?.fwd_ps != null);

  /* industry_avg is a dynamic dict — label = humanized key, values raw ratios. */
  const avgRows = industry_avg
    ? Object.entries(industry_avg)
        .filter(([, v]) => v != null)
        .map(([k, v]) => ({ key: k, label: humanizeKey(k), value: formatRatio(k, v) }))
    : [];

  const nothing =
    !sector_name && !industry_name && avgRows.length === 0 && top_peers.length === 0 && !note;
  if (nothing) return <NoteEmpty>Sector context is not available for {ticker}.</NoteEmpty>;

  return (
    <NotePage>
      {/* ── Masthead ── */}
      <NoteMasthead
        eyebrow="Sector primer"
        title={`${industry_name || sector_name || ticker} — Sector Primer`}
        meta={[
          sector_name ? `Sector: ${sector_name}` : null,
          `Peers ranked by overall score, same sector and market`,
        ]
          .filter(Boolean)
          .join(" · ")}
      >
        {industry_name && sector_name && <Pill>{sector_name}</Pill>}
      </NoteMasthead>

      {/* ── Market dynamics ── */}
      {note?.market_dynamics && (
        <>
          <SectionTitle>Market dynamics</SectionTitle>
          <ProseSection>{note.market_dynamics}</ProseSection>
        </>
      )}

      {/* ── Competitive landscape ── */}
      {Array.isArray(note?.competitive_landscape) && note.competitive_landscape.length > 0 && (
        <>
          <SectionTitle>Competitive landscape</SectionTitle>
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <Th left>Player</Th>
                  <Th left>Exposure</Th>
                  <Th left>Coverage</Th>
                </tr>
              </thead>
              <tbody>
                {note.competitive_landscape.map((c, i) => {
                  const covered = /covered/i.test(c.coverage ?? "");
                  return (
                    <tr key={c.player ?? i}>
                      <Tl>{c.player}</Tl>
                      <Td left tone="muted" wrap>{c.exposure ?? "—"}</Td>
                      <Td left tone={covered ? "pos" : "muted"}>
                        {covered ? "✓ " : "✗ "}
                        {c.coverage ?? "—"}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Scroller>
        </>
      )}

      {/* ── Industry averages ── */}
      {avgRows.length > 0 && (
        <>
          <SectionTitle caption="TTM financial ratios, industry mean">
            Industry averages
          </SectionTitle>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
              gap: "var(--spacing-sm)",
            }}
          >
            {avgRows.map((r) => (
              <Card key={r.key} style={{ padding: "12px 14px", minWidth: 0 }}>
                <div
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: "var(--font-weight-semibold)",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: 6,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={r.label}
                >
                  {r.label}
                </div>
                <div
                  style={{
                    color: "var(--color-text-primary)",
                    fontSize: "var(--font-size-xl)",
                    fontWeight: "var(--font-weight-bold)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {r.value}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* ── Peer comps spread ── */}
      {compsRows.length > 0 && (
        <>
          <SectionTitle caption="forward, consensus-based · the requested stock is highlighted">
            Peer comps spread
          </SectionTitle>
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <Th left>Company</Th>
                  <Th>Price</Th>
                  <Th>Fwd P/E</Th>
                  <Th>EPS growth</Th>
                  <Th>PEG</Th>
                  <Th>Fwd P/S</Th>
                  <Th>Gross margin</Th>
                  <Th>Model rank</Th>
                </tr>
              </thead>
              <tbody>
                {compsRows.map((r, i) => (
                  <tr
                    key={r.ticker ?? i}
                    onClick={() => !r.isSelf && r.ticker && navigate(`/stocks/${r.ticker}/details/summary`)}
                    style={{
                      cursor: r.isSelf ? "default" : "pointer",
                      backgroundColor: r.isSelf ? "var(--color-surface-hover)" : undefined,
                    }}
                  >
                    <Tl style={{ fontWeight: r.isSelf ? "var(--font-weight-bold)" : undefined }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        {r.logo_url && (
                          <img
                            src={r.logo_url}
                            alt=""
                            style={{ width: 18, height: 18, objectFit: "contain" }}
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        )}
                        <span style={{ color: r.isSelf ? "var(--color-text-primary)" : "var(--color-primary)" }}>
                          {r.company_name ? `${r.company_name} (${r.ticker})` : r.ticker}
                        </span>
                        {r.price_check && (
                          <MuiTooltip title={r.price_check} placement="top" arrow>
                            <span style={{ color: "var(--color-warning)", cursor: "help" }}>⚠</span>
                          </MuiTooltip>
                        )}
                      </span>
                    </Tl>
                    <Td tone="muted">{r.price != null ? formatCurrency(r.price) : "—"}</Td>
                    <Td bold={r.isSelf}>{fmtMultiple(r.fwd_pe)}</Td>
                    {/* the growth that nets out of the P/E to give the PEG */}
                    <Td
                      tone={
                        r.eps_growth_fwd_pct == null
                          ? "muted"
                          : Number(r.eps_growth_fwd_pct) >= 0
                          ? "pos"
                          : "neg"
                      }
                      title={
                        r.eps_fy1 != null && r.eps_fy2 != null
                          ? `EPS FY1 ${formatCurrency(r.eps_fy1)} → FY2 ${formatCurrency(r.eps_fy2)}`
                          : undefined
                      }
                    >
                      {r.eps_growth_fwd_pct != null
                        ? fmtPct(r.eps_growth_fwd_pct, { decimals: 1 })
                        : "—"}
                    </Td>
                    <Td bold={r.isSelf}>
                      {fmtPeg(r.peg)}
                      {r.peg_flag && (
                        <MuiTooltip
                          title={`PEG flattered by a ${r.peg_flag} / peak-EPS surge — read with caution.`}
                          placement="top"
                          arrow
                        >
                          <span style={{ color: "var(--color-warning)", cursor: "help" }}> ⚠</span>
                        </MuiTooltip>
                      )}
                    </Td>
                    <Td>{fmtMultiple(r.fwd_ps)}</Td>
                    <Td>{r.gross_margin_pct != null ? fmtPct(r.gross_margin_pct, { signed: false, decimals: 1 }) : "—"}</Td>
                    <Td
                      tone="muted"
                      title={
                        r.model_score != null
                          ? `Factor-model composite ${fmtNum(r.model_score, 2)}`
                          : undefined
                      }
                    >
                      {fmtRank(r.model_rank, r.model_universe)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroller>

          {/* read on PEG — templated server-side; null when own PEG is n/m */}
          {peg_read && (
            <Banner kind="info" style={{ marginTop: "var(--spacing-sm)" }}>
              <strong>Read on PEG: </strong>
              <span style={{ fontWeight: 400 }}>{peg_read}</span>
            </Banner>
          )}
        </>
      )}

      {/* ── Top peers by model grade ── */}
      {top_peers.length > 0 && (
        <>
          <SectionTitle caption="same sector & market, ranked by overall score">
            Top peers
          </SectionTitle>
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <Th left>Ticker</Th>
                  <Th left>Company</Th>
                  <Th>Overall score</Th>
                  <Th>Grade</Th>
                </tr>
              </thead>
              <tbody>
                {top_peers.map((p) => (
                  <tr
                    key={p.ticker}
                    onClick={() => navigate(`/stocks/${p.ticker}/details/summary`)}
                    style={{ cursor: "pointer" }}
                  >
                    <Tl>
                      <span style={{ color: "var(--color-primary)", fontWeight: "var(--font-weight-bold)" }}>
                        {p.ticker}
                      </span>
                    </Tl>
                    <Td left tone="muted" style={{ width: "100%" }}>{p.company_name}</Td>
                    <Td bold>{p.overall_score != null ? fmtNum(p.overall_score, 1) : "—"}</Td>
                    <Td>
                      <GradeChip grade={p.overall_grade} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroller>
          <Banner kind="plain" style={{ marginTop: "var(--spacing-sm)" }}>
            Peers exclude {ticker} itself and are limited to the same sector and market.
          </Banner>
        </>
      )}

      {/* ── Thematic ideas shortlist ── */}
      {Array.isArray(note?.thematic_shortlist) && note.thematic_shortlist.length > 0 && (
        <>
          <SectionTitle>Thematic ideas shortlist</SectionTitle>
          <NoteList items={note.thematic_shortlist} boldKey="name" bodyKeys={["take"]} />
        </>
      )}

      {/* ── Sector risk ── */}
      {note?.sector_risk && (
        <Banner kind="warning" style={{ marginTop: "var(--spacing-md)" }}>
          <strong>Sector risk: </strong>
          <span style={{ fontWeight: 400 }}>{note.sector_risk}</span>
        </Banner>
      )}

      <NoteFooter>
        Industry averages are TTM financial ratios; peer grades and ranks come from the factor
        model, and the comps are forward consensus-based. The market-dynamics, competitive-landscape,
        shortlist and sector-risk sections are written from the latest earnings call. Same sector and
        market only. For internal analysis only — not investment advice.
      </NoteFooter>
    </NotePage>
  );
};

export default SectorContext;
